import { errorMessages } from '~/constants/errorMessages.ts';
import {
  GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM,
  GUARANTEES_SEARCH_CACHE_PREFIX,
} from './form/client/constants.ts';
import {
  identitySourceKeys,
  UNKNOWN_USER_CACHE_KEY,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/constants.ts';
import {
  buildCachedSearchOptions,
  getCachedCounterpartySearch,
  isCounterpartyCodeLengthValid,
  normalizeSearchCode,
  saveCounterpartySearchToCache,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/utils.ts';
import type {
  TAccessTokenPayload,
  TCounterpartyAutocompleteOption,
  TCounterpartySearchCachePayload,
  TIdentitySource,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/types.ts';
import {
  isGuaranteesApprovalLetterPaperExecutionCheck,
} from '~/modules/guarantees/form/application/shared/approvalLetterExecution.ts';
import {
  GuaranteesApplicationStatus as GuaranteesApplicationStatusEnum,
  type GuaranteesApplicationStatus,
  type TGuaranteesApplication,
} from '~/store/guarantees/types.ts';
import { normalizeGuaranteesApplicationStatus } from '~/store/guarantees/utils.ts';
import { jwtDecode } from 'jwt-decode';

const normalizeIdentityValue = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase();

export const getStringValue = (value: unknown): string => String(value ?? '').trim();

const getIdentityFromSource = (source: TIdentitySource): string => {
  if (typeof source === 'string' || typeof source === 'number') {
    return normalizeIdentityValue(source);
  }

  if (!source || typeof source !== 'object') {
    return '';
  }

  const sourceObject = source as Record<string, unknown>;
  const identity = identitySourceKeys
    .map((key) => normalizeIdentityValue(sourceObject[key]))
    .find(Boolean);

  return identity ?? '';
};

const getIdentityFromToken = (accessToken?: string | null): string => {
  if (!accessToken) {
    return '';
  }

  try {
    const payload = jwtDecode<TAccessTokenPayload>(accessToken);

    return (
      normalizeIdentityValue(payload.preferred_username) ||
      normalizeIdentityValue(payload.username) ||
      normalizeIdentityValue(payload.login) ||
      normalizeIdentityValue(payload.email) ||
      normalizeIdentityValue(payload.user_name) ||
      normalizeIdentityValue(payload.userId) ||
      normalizeIdentityValue(payload.uid) ||
      normalizeIdentityValue(payload.sub)
    );
  } catch {
    return '';
  }
};

export const normalizeGuaranteesSearchCode = (value: unknown): string => normalizeSearchCode(value);

export const buildGuaranteesCacheStorageKey = ({
  mode,
  userName,
  token,
}: {
  mode: string;
  userName: string;
  token?: string | null;
}): string => {
  const identityKey =
    getIdentityFromSource(userName) || getIdentityFromToken(token) || UNKNOWN_USER_CACHE_KEY;

  return `${GUARANTEES_SEARCH_CACHE_PREFIX}:${mode}:${identityKey}`;
};
export const getCachedGuaranteesSearch = (
  storageKey: string
): TCounterpartySearchCachePayload | null => getCachedCounterpartySearch(storageKey);

export const saveGuaranteesSearchToCache = (
  storageKey: string,
  code: string
): TCounterpartySearchCachePayload | null => saveCounterpartySearchToCache(storageKey, code);

export const isGuaranteesCounterpartyCodeLengthValid = (value: string): boolean =>
  isCounterpartyCodeLengthValid(value);

export const isGuaranteesAdministratorPath = (pathname?: string | null): boolean =>
  String(pathname ?? '').startsWith('/guarantees/administrator');

export const getGuaranteesErrorMessage = (error?: unknown): string => {
  if (!error || typeof error !== 'object') {
    return errorMessages.SERVICE_UNAVAILABLE;
  }

  const getValidationMessage = (data: unknown): string => {
    if (!Array.isArray(data)) {
      return '';
    }

    return data
      .map((item: { message?: unknown }) =>
        typeof item?.message === 'string' ? item.message.trim() : ''
      )
      .find(Boolean) ?? '';
  };

  const errorLike = error as {
    message?: unknown;
    data?: unknown;
  };
  const validationMessage = getValidationMessage(errorLike.data);
  const dataMessage =
    errorLike.data && typeof errorLike.data === 'object' && !Array.isArray(errorLike.data)
      ? (errorLike.data as { message?: unknown }).message
      : undefined;

  return (
    validationMessage ||
    (typeof errorLike.message === 'string' && errorLike.message) ||
    (typeof dataMessage === 'string' && dataMessage) ||
    errorMessages.SERVICE_UNAVAILABLE
  );
};

export const isManualAmApprovalLetterInProgress = (
  application?: Pick<
    TGuaranteesApplication,
    'applicationStatus' | 'executionCheck' | 'executionChecks'
  > | null,
  isManualTask = false
): boolean =>
  normalizeGuaranteesApplicationStatus(application?.applicationStatus) ===
    GuaranteesApplicationStatusEnum.IN_PROGRESS_AM_APPROVAL_LETTER &&
  (isGuaranteesApprovalLetterPaperExecutionCheck(application as TGuaranteesApplication) ||
    isManualTask);

export const isGuaranteesApplicationVersionMismatch = (error: unknown): boolean => {
  const code =
    (
      error as
        | {
            code?: string;
            errorCode?: string;
            data?: { code?: string; errorCode?: string };
          }
        | undefined
    )?.code ??
    (error as { errorCode?: string } | undefined)?.errorCode ??
    (error as { data?: { code?: string; errorCode?: string } } | undefined)?.data?.code ??
    (error as { data?: { errorCode?: string } } | undefined)?.data?.errorCode;

  return code === 'APPLICATION_VERSION_MISMATCH';
};

export const buildGuaranteesApplicationVersionMismatchModal = (onClick: () => void) => ({
  text: 'Заявку вже оновив хтось інший. Оновіть, будь ласка, вікно.',
  buttonValue: 'Оновити',
  onClick,
});
export const buildGuaranteesCounterpartyPath = (
  contragentId?: number | string,
  isAdministratorFlow = false
): string =>
  isAdministratorFlow
    ? `/guarantees/administrator/contragents/${contragentId}/my`
    : `/guarantees/contragents/${contragentId}/my`;

export const buildGuaranteesApplicationPath = (
  applicationId: number | string,
  contragentId?: number | string | null,
  isAdministratorFlow = false,
  reviewMode?: string
): string => {
  const pathname = isAdministratorFlow
    ? `/guarantees/administrator/application/${applicationId}`
    : `/guarantees/application/${applicationId}`;
  const normalizedContragentId = String(contragentId ?? '').trim();
  const normalizedReviewMode = String(reviewMode ?? '').trim();

  if (!normalizedContragentId && !normalizedReviewMode) {
    return pathname;
  }

  const searchParams = new URLSearchParams();

  if (normalizedContragentId) {
    searchParams.set('contragentId', normalizedContragentId);
  }

  if (normalizedReviewMode) {
    searchParams.set(GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM, normalizedReviewMode);
  }

  return `${pathname}?${searchParams.toString()}`;
};

export const buildGuaranteesApplicationStep2Path = (
  applicationId: number | string,
  contragentId?: number | string | null,
  isAdministratorFlow = false,
  reviewMode?: string
): string => {
  const pathname = isAdministratorFlow
    ? `/guarantees/administrator/application/${applicationId}/step2`
    : `/guarantees/application/${applicationId}/step2`;
  const normalizedContragentId = String(contragentId ?? '').trim();
  const normalizedReviewMode = String(reviewMode ?? '').trim();

  if (!normalizedContragentId && !normalizedReviewMode) {
    return pathname;
  }

  const searchParams = new URLSearchParams();

  if (normalizedContragentId) {
    searchParams.set('contragentId', normalizedContragentId);
  }

  if (normalizedReviewMode) {
    searchParams.set(GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM, normalizedReviewMode);
  }

  return `${pathname}?${searchParams.toString()}`;
};

export const isGuaranteesClientInitialSigningStatus = (
  status?: GuaranteesApplicationStatus | string | null
): boolean =>
  status === GuaranteesApplicationStatusEnum.AWAITING_CLIENT_INITIAL_SIGN ||
  status === GuaranteesApplicationStatusEnum.IN_PROGRESS_CLIENT_INITIAL_SIGN;

export const isGuaranteesClientApprovalLetterSigningStatus = (
  status?: GuaranteesApplicationStatus | string | null
): boolean =>
  status === GuaranteesApplicationStatusEnum.AWAITING_CLIENT_APPROVAL_LETTER_SIGN ||
  status === GuaranteesApplicationStatusEnum.IN_PROGRESS_CLIENT_APPROVAL_LETTER_SIGN;

export const isGuaranteesClientStep3Status = (
  status?: GuaranteesApplicationStatus | string | null
): boolean =>
  isGuaranteesClientApprovalLetterSigningStatus(status);

export const isGuaranteesClientSigningStatus = (
  status?: GuaranteesApplicationStatus | string | null
): boolean =>
  isGuaranteesClientInitialSigningStatus(status) ||
  isGuaranteesClientStep3Status(status);

export const buildGuaranteesApplicationStep3Path = (
  applicationId: number | string,
  contragentId?: number | string | null,
  isAdministratorFlow = false,
  reviewMode?: string
): string => {
  const pathname = isAdministratorFlow
    ? `/guarantees/administrator/application/${applicationId}/step3`
    : `/guarantees/application/${applicationId}/step3`;
  const normalizedContragentId = String(contragentId ?? '').trim();
  const normalizedReviewMode = String(reviewMode ?? '').trim();

  if (!normalizedContragentId && !normalizedReviewMode) {
    return pathname;
  }

  const searchParams = new URLSearchParams();

  if (normalizedContragentId) {
    searchParams.set('contragentId', normalizedContragentId);
  }

  if (normalizedReviewMode) {
    searchParams.set(GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM, normalizedReviewMode);
  }

  return `${pathname}?${searchParams.toString()}`;
};

export const buildGuaranteesApplicationAmApprovalLetterPath = (
  applicationId: number | string,
  contragentId?: number | string | null,
  isAdministratorFlow = false
): string => {
  const pathname = isAdministratorFlow
    ? `/guarantees/administrator/application/${applicationId}/step4`
    : `/guarantees/application/${applicationId}`;
  const normalizedContragentId = String(contragentId ?? '').trim();

  if (!normalizedContragentId) {
    return pathname;
  }

  const searchParams = new URLSearchParams({
    contragentId: normalizedContragentId,
  });

  return `${pathname}?${searchParams.toString()}`;
};

export const buildGuaranteesClientApplicationPathByStatus = (
  applicationId: number | string,
  contragentId?: number | string | null,
  status?: GuaranteesApplicationStatus | string | null
): string => {
  if (isGuaranteesClientInitialSigningStatus(status)) {
    return buildGuaranteesApplicationStep2Path(applicationId, contragentId, false);
  }

  if (isGuaranteesClientStep3Status(status)) {
    return buildGuaranteesApplicationStep3Path(applicationId, contragentId, false);
  }

  return buildGuaranteesApplicationPath(applicationId, contragentId, false);
};

export const buildGuaranteesApplicationAmApprovalLetterManualSignPath = (
  applicationId: number | string,
  contragentId?: number | string | null
): string => {
  const pathname = `/guarantees/administrator/application/${applicationId}/am-approval-letter-manual-sign`;
  const normalizedContragentId = String(contragentId ?? '').trim();

  if (!normalizedContragentId) {
    return pathname;
  }

  const searchParams = new URLSearchParams({
    contragentId: normalizedContragentId,
  });

  return `${pathname}?${searchParams.toString()}`;
};

export const buildGuaranteesApplicationAmApprovalLetterClientSignedPath = (
  applicationId: number | string,
  contragentId?: number | string | null
): string => {
  const pathname = `/guarantees/administrator/application/${applicationId}/am-approval-letter-client-signed`;
  const normalizedContragentId = String(contragentId ?? '').trim();

  if (!normalizedContragentId) {
    return pathname;
  }

  const searchParams = new URLSearchParams({
    contragentId: normalizedContragentId,
  });

  return `${pathname}?${searchParams.toString()}`;
};

export const buildGuaranteesApplicationInternalApprovalLetterPath = (
  applicationId: number | string,
  contragentId?: number | string | null,
  approvalType = 'udbtf'
): string => {
  const normalizedApprovalType = String(approvalType).trim();
  const pathname = `/guarantees/administrator/application/${applicationId}/internal-approval-letter/${normalizedApprovalType}`;
  const normalizedContragentId = String(contragentId ?? '').trim();

  if (!normalizedContragentId) {
    return pathname;
  }

  const searchParams = new URLSearchParams({
    contragentId: normalizedContragentId,
  });

  return `${pathname}?${searchParams.toString()}`;
};

export const buildGuaranteesApplicationUakdoControlPath = (
  applicationId: number | string,
  contragentId?: number | string | null
): string => {
  const pathname = `/guarantees/administrator/application/${applicationId}/uakdo-control`;
  const normalizedContragentId = String(contragentId ?? '').trim();

  if (!normalizedContragentId) {
    return pathname;
  }

  const searchParams = new URLSearchParams({
    contragentId: normalizedContragentId,
  });

  return `${pathname}?${searchParams.toString()}`;
};

export const buildGuaranteesApplicationUakdoControllerControlPath = (
  applicationId: number | string,
  contragentId?: number | string | null
): string => {
  const pathname = `/guarantees/administrator/application/${applicationId}/uakdo-controller-control`;
  const normalizedContragentId = String(contragentId ?? '').trim();

  if (!normalizedContragentId) {
    return pathname;
  }

  const searchParams = new URLSearchParams({
    contragentId: normalizedContragentId,
  });

  return `${pathname}?${searchParams.toString()}`;
};

export const buildGuaranteesApplicationUdbtfGuaranteeScanCopyPath = (
  applicationId: number | string,
  contragentId?: number | string | null
): string => {
  const pathname = `/guarantees/administrator/application/${applicationId}/udbtf-guarantee-scan-copy`;
  const normalizedContragentId = String(contragentId ?? '').trim();

  if (!normalizedContragentId) {
    return pathname;
  }

  const searchParams = new URLSearchParams({
    contragentId: normalizedContragentId,
  });

  return `${pathname}?${searchParams.toString()}`;
};

export const buildGuaranteesApplicationUsapokApprovalPath = (
  applicationId: number | string,
  contragentId?: number | string | null
): string => {
  const pathname = `/guarantees/administrator/application/${applicationId}/usapok-approval`;
  const normalizedContragentId = String(contragentId ?? '').trim();

  if (!normalizedContragentId) {
    return pathname;
  }

  const searchParams = new URLSearchParams({
    contragentId: normalizedContragentId,
  });

  return `${pathname}?${searchParams.toString()}`;
};
export const buildGuaranteesCachedSearchOptions = ({
  cachedSearch,
  dateFormat,
  datePrefix,
}: {
  cachedSearch: TCounterpartySearchCachePayload | null;
  dateFormat: string;
  datePrefix: string;
}): TCounterpartyAutocompleteOption[] =>
  buildCachedSearchOptions({
    cachedSearch,
    dateFormat,
    datePrefix,
  });
